import Vue from 'vue';
import { Getter, State, Mutation } from 'vuex-simple';
import { ThemeType } from '@/HostApp/types/ThemeType';
import { ux1Data } from '@/Common/Utils/General/Modules';
import { customTextFonts, ux1FrameworkDefaultColorsVariablesCssSelector } from '@/Common/Environment/const';

import tokensMatches, { ITokenMatches } from '@/HostApp/services/DesignTokens/tokensMatches';
import VariablesSass from '@/HostApp/styles/variables.module.scss';
import cssBundlerModule from '@/HostApp/services/CSSBundler/CSSBundlerModule';

export class DesignTokensModule {
	constructor() {
		this.tokens = Object.keys(VariablesSass).reduce((acc, key) => {
			const keyInCamelCase = ux1Data.changeCase.camelCase(key);
			acc[keyInCamelCase] = VariablesSass[key];

			return acc;
		}, {} as Common.IMap<string>);
	}

	@State()
	public themeType: ThemeType = ThemeType.Default;

	@State()
	public defaultThemeVariables: Record<string, string> = {};

	@State()
	public themeVariables: Record<string, string> = {};

	@State()
	public ux1FrameworkCss: CSSRule | null = null;

	@State()
	private readonly tokens: Common.IMap<string> = {};

	@State()
	public api: Partial<DesignTokensModule> = {
		setTokenValue: (key: string, value: string) => {
			this.setTokenValue(key, value);
		}
	};

	@Getter()
	public get cssVariables(): Common.IMap<string> {
		return Object.keys(this.tokens).reduce((acc, key) => {
			const value = this.tokens[key];
			const varName = `--${ux1Data.changeCase.paramCase(key)}`;
			acc[varName] = value;
			return acc;
		}, {} as Common.IMap<string>);
	}

	@Getter()
	public get getIsDefaultSkin(): boolean {
		return this.themeType === ThemeType.Default;
	}

	@Mutation()
	public setDefaultThemeVariables(defaultThemeVariables: Record<string, string>): void {
		this.defaultThemeVariables = defaultThemeVariables;
	}

	@Mutation()
	public setThemeType(themeType: ThemeType): void {
		this.themeType = themeType;
		// For avoiding circular dependencies
		cssBundlerModule.setThemeType(themeType);
	}

	@Mutation()
	public setThemeVariables(css: CSSStyleSheet): void {
		const themeVariables = this.getThemeVariables(css, ':root');
		this.themeVariables = themeVariables;
	}

	@Mutation()
	public setUx1FrameworkCss(css: CSSStyleSheet): void {
		const cssRootRule = Array.from(css.cssRules).find((cssRule) =>
			(cssRule as CSSStyleRule).selectorText?.includes(ux1FrameworkDefaultColorsVariablesCssSelector)
		);
		if (cssRootRule) {
			this.ux1FrameworkCss = cssRootRule;
			const defaultThemeVariables: Record<string, string> = this.getDefaultThemeVariables(cssRootRule);
			this.setDefaultThemeVariables(defaultThemeVariables);
		}
	}

	public mergeVariablesWithTheme(bootstrapCss: CSSStyleSheet): void {
		const values = this.getValuesFromStyleSheet(bootstrapCss, tokensMatches);
		for (const [key, value] of Object.entries(values)) {
			this.setTokenValue(key, value);
		}
	}

	private getValuesFromStyleSheet(bootstrapCss: CSSStyleSheet, matches: ITokenMatches): Common.IMap<string> {
		const cssRules = Array.from(bootstrapCss.cssRules) as Array<CSSStyleRule>;
		const tokensMatchesEntries = Object.entries(matches);
		const result: Record<string, string> = {};

		for (const cssRule of cssRules) {
			for (const [key, { rule, property }] of tokensMatchesEntries) {
				const matchedStyle = cssRule.style?.[property];
				if (matchedStyle && cssRule.selectorText === rule) {
					result[key] = matchedStyle as string;
				}
			}

			if (cssRule instanceof CSSFontFaceRule) {
				if (cssRule.style['fontFamily'] === 'DefaultTextsFont') {
					if (cssRule.style['fontWeight'] === 'normal') {
						result[customTextFonts.regular] = cssRule.cssText;
					} else if (cssRule.style['fontWeight'] === 'bold') {
						result[customTextFonts.bold] = cssRule.cssText;
					}
				}
			}
		}

		return result;
	}

	public setTokenValue(key: string, value: string): void {
		Vue.set(this.tokens, key, value);
		const varName = [customTextFonts.regular, customTextFonts.bold].includes(key)
			? key
			: `--${ux1Data.changeCase.paramCase(key)}`;
		cssBundlerModule.setCSSVariableValue(varName, value);
	}

	public getThemeVariables(css: CSSStyleSheet, cssSelector: string): Record<string, string> {
		const themeCssText = Array.from(css.cssRules).find((rule) => rule.cssText.includes(cssSelector));
		if (themeCssText) {
			const themeVariables = themeCssText.cssText
				.replace(`${cssSelector} {`, '')
				.replace('}', '')
				.trim()
				.split(';')
				.filter(Boolean)
				.reduce((acc, rule) => {
					const [property, value] = rule.split(':').map((item) => item.trim());
					acc[property] = value;
					return acc;
				}, {} as Record<string, string>);
			return themeVariables;
		} else {
			return {};
		}
	}

	public getDefaultThemeVariables(cssRule: CSSRule): Record<string, string> {
		const themeVariablesKeys = [
			'--background-color-stage',
			'--background-color-container',
			'--color-border',
			'--color-brand',
			'--color-danger',
			'--color-info',
			'--color-primary',
			'--color-success',
			'--color-text',
			'--color-text-secondary',
			'--color-warning',
			'--sidebar-background-color',
			'--sidebar-item-text',
			'--sidebar-item-text-active',
			'--top-bar-background-color',
			'--top-bar-item-text'
		];
		const themeVariables = themeVariablesKeys.map((key) => {
			const value = cssRule.style.getPropertyValue(key);
			return [key, value];
		});
		return Object.fromEntries(themeVariables);
	}
}

const designTokensModule = new DesignTokensModule();

export default designTokensModule;
